import type {
  PortalAccessLevel,
  PortalSection,
  PortalSectionCategory,
  WorkflowCapabilityId,
} from '@statura/types';

export interface PortalSectionDefinition {
  key: PortalSection;
  label: string;
  route: string;
  category: PortalSectionCategory;
  legislativelyRequired: boolean;
  legislativeRef?: string;
  defaultEnabled: boolean;
  workflowCapability: WorkflowCapabilityId;
  contactPersonVisible: boolean;
}

export type PortalSectionVisibilityConfig = Record<PortalSection, boolean>;

export interface PortalSectionAccessState {
  allowed: boolean;
  providerVisible: boolean;
  roleAllowed: boolean;
}

export const PORTAL_SECTION_DEFINITIONS: readonly PortalSectionDefinition[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    route: '/dashboard',
    category: 'overview',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(e)',
    defaultEnabled: true,
    workflowCapability: 'portal.care.view',
    contactPersonVisible: true,
  },
  {
    key: 'care_plan',
    label: 'Care Plan',
    route: '/care-plan',
    category: 'care',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(e), Quality Standard 3',
    defaultEnabled: true,
    workflowCapability: 'portal.care.view',
    contactPersonVisible: false,
  },
  {
    key: 'visits',
    label: 'Visits',
    route: '/visits',
    category: 'care',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(e)',
    defaultEnabled: true,
    workflowCapability: 'portal.care.view',
    contactPersonVisible: true,
  },
  {
    key: 'medications',
    label: 'Medications',
    route: '/medications',
    category: 'care',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(e), Quality Standard 3',
    defaultEnabled: true,
    workflowCapability: 'portal.care.view',
    contactPersonVisible: false,
  },
  {
    key: 'incidents',
    label: 'Incidents & Safety',
    route: '/incidents',
    category: 'safety',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(d) Statement of Rights, s 164 incident management',
    defaultEnabled: true,
    workflowCapability: 'portal.safety.view',
    contactPersonVisible: false,
  },
  {
    key: 'restrictive_practices',
    label: 'Restrictive Practices',
    route: '/restrictive-practices',
    category: 'safety',
    legislativelyRequired: true,
    legislativeRef: 's 162, Quality Standard 5',
    defaultEnabled: true,
    workflowCapability: 'portal.safety.view',
    contactPersonVisible: false,
  },
  {
    key: 'advance_care',
    label: 'Advance Care Planning',
    route: '/advance-care',
    category: 'safety',
    legislativelyRequired: false,
    defaultEnabled: true,
    workflowCapability: 'portal.care.view',
    contactPersonVisible: false,
  },
  {
    key: 'budget',
    label: 'Budget & Contributions',
    route: '/budget',
    category: 'financial',
    legislativelyRequired: true,
    legislativeRef: 'SAH Rules Ch4',
    defaultEnabled: true,
    workflowCapability: 'portal.financial.view',
    contactPersonVisible: false,
  },
  {
    key: 'invoices',
    label: 'Invoices & Statements',
    route: '/invoices',
    category: 'financial',
    legislativelyRequired: true,
    legislativeRef: 'SAH Rules Ch4',
    defaultEnabled: true,
    workflowCapability: 'portal.financial.view',
    contactPersonVisible: false,
  },
  {
    key: 'messages',
    label: 'Messages',
    route: '/messages',
    category: 'communication',
    legislativelyRequired: false,
    defaultEnabled: true,
    workflowCapability: 'portal.messaging',
    contactPersonVisible: true,
  },
  {
    key: 'feedback',
    label: 'Feedback & Complaints',
    route: '/feedback',
    category: 'communication',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(i)-(j)',
    defaultEnabled: true,
    workflowCapability: 'portal.feedback',
    contactPersonVisible: true,
  },
  {
    key: 'my_rights',
    label: 'My Rights',
    route: '/my-rights',
    category: 'account',
    legislativelyRequired: true,
    legislativeRef: 'ss 22-23',
    defaultEnabled: true,
    workflowCapability: 'portal.rights.view',
    contactPersonVisible: true,
  },
  {
    key: 'my_agreement',
    label: 'My Agreement',
    route: '/my-agreement',
    category: 'account',
    legislativelyRequired: true,
    legislativeRef: 'SAH Rules',
    defaultEnabled: true,
    workflowCapability: 'portal.documents.view',
    contactPersonVisible: false,
  },
  {
    key: 'documents',
    label: 'Documents',
    route: '/documents',
    category: 'account',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(f)',
    defaultEnabled: true,
    workflowCapability: 'portal.documents.view',
    contactPersonVisible: false,
  },
  {
    key: 'profile',
    label: 'Profile & Access',
    route: '/profile',
    category: 'account',
    legislativelyRequired: true,
    legislativeRef: 's 23(1)(h),(k)',
    defaultEnabled: true,
    workflowCapability: 'portal.rights.view',
    contactPersonVisible: true,
  },
] as const;

export const OPTIONAL_PORTAL_SECTIONS = PORTAL_SECTION_DEFINITIONS.filter(
  (section) => !section.legislativelyRequired,
).map((section) => section.key) as PortalSection[];

export function getPortalSectionDefinition(section: PortalSection): PortalSectionDefinition {
  const definition = PORTAL_SECTION_DEFINITIONS.find((item) => item.key === section);
  if (!definition) {
    throw new Error(`Unknown portal section: ${section}`);
  }
  return definition;
}

export function buildPortalSectionConfig(
  providerConfig?: Record<string, boolean> | null,
): PortalSectionVisibilityConfig {
  return PORTAL_SECTION_DEFINITIONS.reduce((acc, section) => {
    if (section.legislativelyRequired) {
      acc[section.key] = true;
      return acc;
    }

    if (providerConfig && section.key in providerConfig) {
      acc[section.key] = providerConfig[section.key] ?? section.defaultEnabled;
      return acc;
    }

    acc[section.key] = section.defaultEnabled;
    return acc;
  }, {} as PortalSectionVisibilityConfig);
}

export function isPortalSectionVisible(
  section: PortalSection,
  providerConfig?: Record<string, boolean> | null,
): boolean {
  return buildPortalSectionConfig(providerConfig)[section];
}

export function getPortalSectionAccessState(
  section: PortalSection,
  accessLevel: PortalAccessLevel,
  providerConfig?: Record<string, boolean> | null,
): PortalSectionAccessState {
  const definition = getPortalSectionDefinition(section);
  const providerVisible = isPortalSectionVisible(section, providerConfig);
  const roleAllowed =
    accessLevel === 'CLIENT' || accessLevel === 'NOMINEE' || definition.contactPersonVisible;

  return {
    allowed: providerVisible && roleAllowed,
    providerVisible,
    roleAllowed,
  };
}

export function canPortalAccessSection(
  section: PortalSection,
  accessLevel: PortalAccessLevel,
  providerConfig?: Record<string, boolean> | null,
): boolean {
  return getPortalSectionAccessState(section, accessLevel, providerConfig).allowed;
}

export function getVisiblePortalSections(
  accessLevel: PortalAccessLevel,
  providerConfig?: Record<string, boolean> | null,
): PortalSection[] {
  return PORTAL_SECTION_DEFINITIONS.filter((section) =>
    canPortalAccessSection(section.key, accessLevel, providerConfig),
  ).map((section) => section.key);
}

export function getPortalSectionFromPath(pathname: string): PortalSection | null {
  const withoutQuery = pathname.split('?')[0] ?? pathname;
  const withoutHash = withoutQuery.split('#')[0] ?? withoutQuery;
  const normalized = withoutHash.replace(/\/+$/, '') || '/dashboard';
  const definition = PORTAL_SECTION_DEFINITIONS.filter(
    (section) => normalized === section.route || normalized.startsWith(`${section.route}/`),
  ).sort((left, right) => right.route.length - left.route.length)[0];
  return definition?.key ?? null;
}

export function getPersistedPortalConfig(
  providerConfig?: Record<string, boolean> | null,
): Record<string, boolean> {
  const resolved = buildPortalSectionConfig(providerConfig);
  return OPTIONAL_PORTAL_SECTIONS.reduce<Record<string, boolean>>((acc, section) => {
    acc[section] = resolved[section];
    return acc;
  }, {});
}
